import type { WebTask } from '../../../lib/tasks';
import { compareForTaskList, recentTasks } from '../../../lib/agent-task-buckets';
import { visibleTaskTabs, type TabKey, type TaskTab } from './buckets';
import { actionNeededCount } from '../../../lib/action-needed-count';
import type { OpenAsksRead, WebOpenAsk } from '../../../lib/open-asks';
import { interleaveByRecency } from '../../../lib/interleave-by-recency';
import { chaseAsksOf, isChaseAsk, type ChaseAsk } from '../../../lib/chase-asks';

// Pure logic behind the one tabbed Tasks renderer (`TasksBody`) — extracted so the tab/filter/ordering
// behaviour is unit-testable without a DOM. Mirrors native's `flatByUpdated` / tab resolution 1:1.

/**
 * The "All" tab's list: every task in the recency window, optionally narrowed to one agent, in the shared
 * reading order — unfinished work above finished, newest-updated within each half. The ordering rule lives
 * in `agent-task-buckets` (`compareForTaskList`) because every surface that lists tasks has to apply the
 * same one; this function only adds the agent filter. `now` injectable for tests.
 */
export function flatByUpdated(tasks: WebTask[], agentId?: string, now: number = Date.now()): WebTask[] {
  return recentTasks(tasks, now)
    .filter((task) => !agentId || task.agentId === agentId)
    .sort((a, b) => compareForTaskList(a, b, now));
}

/**
 * The tab set the renderer shows: the visible status tabs for these tasks.
 *
 * ⚠️ The leading catch-all **All tasks** tab is HIDDEN (product decision, 2026-09-02 — it read as
 * confusing next to the status tabs). Nothing else about it was removed: `TabKey` still carries `'all'`,
 * `flatByUpdated` still builds its list, `TasksBody` still counts it, and `TaskTabs` still knows its
 * label — so re-enabling it is putting `'all'` back at the head of this array (and of `TasksBody`'s
 * `tabs`). Kept in sync with native, which hides its own `TaskTabKey.all` the same way.
 */
export function taskTabKeys(tasks: WebTask[], now: number = Date.now()): TabKey[] {
  // return ['all', ...visibleTaskTabs(tasks, now)];  // ← restores the hidden "All tasks" tab
  return [...visibleTaskTabs(tasks, now)];
}

/**
 * Which tab is active. A user's pick wins (while it's still a rendered tab); otherwise a `?tab=<status>`
 * deep-link selects that status tab if it's visible; otherwise the default is the first NON-EMPTY rendered
 * tab, falling back to the first rendered one.
 *
 * The default used to be the hidden `all` catch-all, which could never be empty. Landing a generic "see
 * all tasks" entry on an empty Action-needed tab while 14 tasks sit one tab over is the regression that
 * rule prevents — it is the same `firstNonEmpty` the buckets module and both phones already apply. The
 * last resort is `tabs[0]` rather than a hard-coded `'waiting'` so hiding (or restoring) a tab can never
 * leave the strip with nothing selected.
 */
export function resolveActiveTab(
  picked: TabKey | null,
  requested: TaskTab | undefined,
  tabs: TabKey[],
  statusTabs: TaskTab[],
  counts: Partial<Record<TabKey, number>> = {},
): TabKey {
  if (picked && tabs.includes(picked)) return picked;
  if (requested && statusTabs.includes(requested)) return requested;
  return tabs.find((tab) => (counts[tab] ?? 0) > 0) ?? tabs[0] ?? 'waiting';
}

/**
 * Whether the Action needed tab lists the account's OPEN ASKS beside its tasks, and draws the "N people are waiting
 * for your answer" note above them (`native-apps/shared/outreach-needs-you/contract.json` rule 5). The island counts
 * the asks and the notification ledger pushes "Action needed" for them, so a tab that leaves them out is a tab the
 * push leads nowhere (Monday 13356869216). (The outreach-only questions it also used to list are people now: their
 * thread is NEEDS_YOU on the Outreach screen, which the note points at.)
 *
 * Only on the UNSCOPED view: an ask, like a person, belongs to no Space and to no agent but the Maestro, so a
 * Space-scoped host, an agent-scoped host, or a Space / agent filter the reader picked has nothing of it to
 * show — listing it there would put a row under a filter it does not match. `hostWorkspaceId` is a host's
 * SERVER scope only (a filterable page's Space is a selection, and arrives in `filters`). Mirrors iOS
 * `AgentTasksScreen.listsOutreachQuestions` and Android `listsOutreachQuestions`.
 */
export function listsOutreachQuestions(scope: {
  hostWorkspaceId?: string;
  hostAgentId?: string;
  filters: { workspaceId: string; agentId: string };
}): boolean {
  return !scope.hostWorkspaceId && !scope.hostAgentId && !scope.filters.workspaceId && !scope.filters.agentId;
}

/**
 * One card the Action needed tab draws beside its task rows: an open ask. The outreach-only question card
 * (`kind: 'outreach'`) is gone — outreach-needs-you contract rule 5: "no longer listed and no longer counted".
 */
export type ActionNeededCard = { kind: 'ask'; ask: WebOpenAsk };

/**
 * The Action needed tab's cards and its badge number. The number is THE shared count
 * (`lib/action-needed-count.ts`, the same function behind the island pill's TASKS half): question CARDS over the
 * waiting tasks (one question blocking three tasks is 1) plus the open asks, minus what a waiting task already asks.
 * The cards are the asks. Pass a `null` read on a scoped view: the count is then the question cards alone.
 *
 * The chaser's OWED items are NOT cards among the rows: they are `chaseAsks`, the "While you were away" group
 * drawn after the list, in its own order (`lib/chase-asks.ts`). They are in `count` only at critical / high stakes.
 */
export function actionNeededCards(
  waiting: { decisionPrompts?: { decisionId: string }[] }[],
  asks: OpenAsksRead | null | undefined,
): { cards: ActionNeededCard[]; chaseAsks: ChaseAsk[]; count: number; extraCount: number } {
  const needed = actionNeededCount(waiting, asks);
  return {
    cards: needed.asksKept.filter((ask) => !isChaseAsk(ask)).map((ask): ActionNeededCard => ({ kind: 'ask', ask })),
    chaseAsks: chaseAsksOf(needed.asksKept),
    count: needed.tasks,
    extraCount: needed.openAsks,
  };
}

/** When a card's ask was raised — the time the interleave reads. */
export function actionNeededCardAt(card: ActionNeededCard): string | undefined {
  return card.ask.createdDate;
}

/** A card's stable identity (React key, DOM hook). */
export function actionNeededCardKey(card: ActionNeededCard): string {
  return `ask:${card.ask.id}`;
}

/** Is this item of the interleaved list a card (rather than a task row)? */
export function isActionNeededCard(item: unknown): item is ActionNeededCard {
  return (item as { kind?: unknown } | null)?.kind === 'ask';
}

/**
 * The Action needed tab's list: its task rows in their own order, with the cards slotted in BY RECENCY — the
 * island's rule (`interleaveByRecency`), a row's time being its first task's `updatedDate`.
 */
export function interleaveActionNeeded<R extends { tasks: { updatedDate?: string | Date }[] }>(
  rows: R[],
  cards: ActionNeededCard[],
): (R | ActionNeededCard)[] {
  return interleaveByRecency(rows, (row) => row.tasks[0]?.updatedDate, cards, actionNeededCardAt);
}
